import type { Suggestion } from "./types";

// Momentum kinds strong enough to justify an unprompted peek (spec §5.1).
const SIGNAL_MOMENTUM_KINDS = ["recent_play"];

/** Pure decision: does this poll's suggestion set carry a "signal" worth an
 * unprompted peek? `index.ts` calls this after the one-shot intro attempt and
 * only REPORTS the result to `renderer.maybeSelfOpenPeek("signal")` — every cap,
 * cooldown, and suppressor lives in the renderer.
 *
 * `previousTopId` is `null` when there is no prior poll to compare against (the
 * very first time this function is consulted in a session) — in that case a
 * "changed top pick" can never be true, since there was no previous pick. Passing
 * `""` as a sentinel instead of `null` was the Task 9 bug: an empty string looks
 * like "the top pick used to be nothing" and any real top id then reads as a
 * change, firing a spurious signal on the first real comparison. */
export function shouldSelfOpenSignal(
  suggestions: Suggestion[],
  previousTopId: string | null,
): boolean {
  const top = suggestions[0];
  const hasMomentumSignal = suggestions.some(
    (s) => s.momentum && SIGNAL_MOMENTUM_KINDS.includes(s.momentum.kind),
  );
  const topChanged =
    previousTopId !== null && Boolean(top) && top.id !== previousTopId;
  return hasMomentumSignal || topChanged;
}

/** The top suggestion's id, or the previous one if this poll returned none —
 * mirrors the "keep last known top" behavior index.ts wants when a poll is
 * momentarily empty. */
export function nextTopId(
  suggestions: Suggestion[],
  previousTopId: string | null,
): string | null {
  const top = suggestions[0];
  return top ? top.id : previousTopId;
}

/** The first suggestion whose server lane is one the self-open path accepts. */
function isQualified(s: Suggestion): boolean {
  return (s.advice_lane === "current_match" || s.advice_lane === "user_priority" ||
    // ⭐ P3 (2026-10-08): a ★ fixture/team kicking off within the hour.
    s.advice_lane === "kickoff_soon" ||
    s.advice_lane === "crowd_momentum") && typeof s.trigger_id === "string" && s.trigger_id.length > 0;
}

function qualifiedCandidate(suggestions: Suggestion[]): Suggestion | undefined {
  return suggestions.find(isQualified);
}

/** Return the first server-qualified trigger accepted by the self-open path. */
export function qualifiedTriggerId(suggestions: Suggestion[]): string | null {
  return qualifiedCandidate(suggestions)?.trigger_id ?? null;
}

/** What a qualified poll decision needs downstream.
 *
 * ⛔ TWO IDS, AND THEY ARE NOT INTERCHANGEABLE. `triggerId` is the server's
 * 16-hex hash of (lane, kind, event, minute bucket) and keys the cross-remount
 * suppression; `cardId` is the suggestion the renderer must OPEN ON. Returning
 * the trigger id alone was silently fatal: `showPeek` matches its target with
 * `s.id === targetId`, so a trigger id never matched, the index fell through to
 * the chip's card, and the qualified open landed on a pick that had not
 * qualified — the exact defect the target parameter was added to fix. */
export interface QualifiedOpen {
  triggerId: string;
  cardId: string;
  eventId: string;
}

/** Return every server-qualified trigger accepted by the self-open path. */
export function qualifiedOpenTargets(
  suggestions: Suggestion[],
  qualifiedSelfOpen: boolean,
  signalSelfOpen: boolean,
): QualifiedOpen[] {
  if (!qualifiedSelfOpen || !signalSelfOpen) return [];
  return suggestions.filter((s) => isQualified(s) && Boolean(s.id)).map((s) => ({
    triggerId: s.trigger_id as string, cardId: s.id, eventId: s.event_id ?? s.id }));
}

export function qualifiedOpenTarget(
  suggestions: Suggestion[],
  qualifiedSelfOpen: boolean,
  signalSelfOpen: boolean,
): QualifiedOpen | null {
  return qualifiedOpenTargets(suggestions, qualifiedSelfOpen, signalSelfOpen)[0] ?? null;
}

/** What to do with a qualified trigger's suppression key when its peek closes.
 *
 * ⛔ THE KEY IS WRITTEN WHEN THE PEEK OPENS, not when it times out. The
 * self-open caps (`PEEK_SELF_OPEN_SIGNAL_CAP`) live on the renderer INSTANCE
 * and this SPA remounts us constantly — `sessionStorage` is the only brake that
 * survives a remount. Recording the suppression only after a `timeout` close
 * means an unmount before the 20s retract leaves nothing written, the fresh
 * renderer's cap starts at zero, and the same trigger opens again every mount.
 *
 * ⛔ A CLOSE IS A DISMISSAL, AND A DISMISSAL IS FINAL (owner, 2026-09-21:
 * "dismiss means dismiss and same card shouldn't appear again"). `close` used
 * to CLEAR the key, so the sequence was: card interrupts → visitor closes it
 * → SPA remounts on the next route change → the same unchanged trigger
 * interrupts again, with no limit, because the renderer-instance caps died with
 * the old mount. The peek still FOLDS rather than hides (the card stays one tap
 * away on the launcher) — what is suppressed is the unprompted interruption.
 *
 * What still CLEARS is engagement: the visitor took the pick (`added`) or the
 * deck ran out under them (`exhausted`). `all_picks` is retired but kept here so
 * an immutable older bundle still closing with it behaves as it always did.
 * `retract` and `gate` are NOT reactions — `retract` is the default reason a
 * defensive `hidePeek()` carries (teardown, expand, a poll that emptied the
 * deck), which is exactly the remount path, and `gate` is the sportsbook
 * leaving the screen. Treating either as engagement would re-open the loop. */
export type SuppressionAction = "keep" | "clear";

const DELIBERATE_REACTIONS = ["added", "all_picks", "exhausted"];

export function suppressionAfterClose(reason: string): SuppressionAction {
  return DELIBERATE_REACTIONS.includes(reason) ? "clear" : "keep";
}

export function qualifiedSuppressionKey(apiKey: string, triggerId: string, cardId: string, eventId: string): string {
  return `bc_qualified_open:${apiKey}:${triggerId}:${eventId}:${cardId}`;
}
